import {
  type InsertClientDailyMetric,
  insertClientDailyMetricSchema,
} from "@/api/db/schema/client-daily-metrics";
import { clientGoalService } from "@/api/services/client-goal.service";
import { clientMetricService } from "@/api/services/client-metric.service";
import {
  coachingClientService,
  isCoachingClient,
} from "@/api/services/coaching-client.service";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

// Check if current user is a coaching client
export const amICoachingClientFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    const isClient = await isCoachingClient(context.auth.userId);
    const assignment = await coachingClientService.getUserCoachingAssignment(
      context.auth.userId,
    );

    return {
      status: 200,
      body: {
        isCoachingClient: isClient,
        assignment: assignment,
      },
    };
  });

export const amICoachingClientOptions = () =>
  queryOptions({
    queryKey: ["amICoachingClient"],
    queryFn: () => amICoachingClientFn(),
    staleTime: 1000 * 60 * 5,
  });

// Get today's metric
export const getTodayMetricFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    const isClient = await isCoachingClient(context.auth.userId);

    if (!isClient) {
      return {
        status: 403,
        body: { message: "Bu özellik sadece koçluk danışanları içindir" },
      };
    }

    const todayMetric = await clientMetricService.getTodayMetric(
      context.auth.userId,
    );

    return {
      status: 200,
      body: {
        hasEnteredToday: !!todayMetric,
        metric: todayMetric,
      },
    };
  });

export const getTodayMetricOptions = () =>
  queryOptions({
    queryKey: ["getTodayMetric"],
    queryFn: () => getTodayMetricFn(),
    staleTime: 1000 * 30, // 30 seconds
  });

// Get metric by date
const getMetricByDateSchema = z.object({
  date: z.string(),
});

export const getMetricByDateFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getMetricByDateSchema)
  .handler(async ({ context, data }) => {
    const isClient = await isCoachingClient(context.auth.userId);

    if (!isClient) {
      return {
        status: 403,
        body: { message: "Bu özellik sadece koçluk danışanları içindir" },
      };
    }

    const metric = await clientMetricService.getMetricByDate(
      context.auth.userId,
      data.date,
    );

    return {
      status: 200,
      body: {
        hasEntered: !!metric,
        metric: metric,
      },
    };
  });

export const getMetricByDateOptions = (date: string) =>
  queryOptions({
    queryKey: ["getMetricByDate", date],
    queryFn: () => getMetricByDateFn({ data: { date } }),
    staleTime: 1000 * 30,
  });

// Get my goals
export const getMyGoalsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    const isClient = await isCoachingClient(context.auth.userId);

    if (!isClient) {
      return {
        status: 403,
        body: { message: "Bu özellik sadece koçluk danışanları içindir" },
      };
    }

    const goals = await clientGoalService.getClientGoals(context.auth.userId);

    return {
      status: 200,
      body: {
        goals: goals,
      },
    };
  });

export const getMyGoalsOptions = () =>
  queryOptions({
    queryKey: ["getMyGoals"],
    queryFn: () => getMyGoalsFn(),
    staleTime: 1000 * 60 * 5,
  });

// Submit daily metric
export const submitDailyMetricFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertClientDailyMetricSchema)
  .handler(async ({ context, data }) => {
    const isClient = await isCoachingClient(context.auth.userId);

    if (!isClient) {
      return {
        status: 403,
        body: { message: "Bu özellik sadece koçluk danışanları içindir" },
      };
    }

    const metric = await clientMetricService.submitDailyMetric(
      context.auth.userId,
      data as InsertClientDailyMetric,
    );

    return {
      status: 200,
      body: {
        message: "Günlük verileriniz kaydedildi",
        metric: metric,
      },
    };
  });

// Get my metrics history
const getMyMetricsSchema = z.object({
  days: z.number().min(1).max(365).default(30),
});

export const getMyMetricsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getMyMetricsSchema)
  .handler(async ({ context, data }) => {
    const isClient = await isCoachingClient(context.auth.userId);

    if (!isClient) {
      return {
        status: 403,
        body: { message: "Bu özellik sadece koçluk danışanları içindir" },
      };
    }

    const metrics = await clientMetricService.getMetricsHistory(
      context.auth.userId,
      data.days,
    );

    return {
      status: 200,
      body: {
        metrics: metrics,
      },
    };
  });

export const getMyMetricsOptions = (days: number = 30) =>
  queryOptions({
    queryKey: ["getMyMetrics", days],
    queryFn: () => getMyMetricsFn({ data: { days } }),
    staleTime: 1000 * 60,
  });

// Get weight chart data
const getWeightChartSchema = z.object({
  days: z.number().min(1).max(365).default(30),
});

export const getWeightChartFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getWeightChartSchema)
  .handler(async ({ context, data }) => {
    const isClient = await isCoachingClient(context.auth.userId);

    if (!isClient) {
      return {
        status: 403,
        body: { message: "Bu özellik sadece koçluk danışanları içindir" },
      };
    }

    const chartData = await clientMetricService.getWeightChartData(
      context.auth.userId,
      data.days,
    );

    return {
      status: 200,
      body: {
        chartData: chartData,
      },
    };
  });

export const getWeightChartOptions = (days: number = 30) =>
  queryOptions({
    queryKey: ["getWeightChart", days],
    queryFn: () => getWeightChartFn({ data: { days } }),
    staleTime: 1000 * 60,
  });
